import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useProfile } from "../../contexts/ProfileContext.tsx";
import TabelaRedux from "../../contexts/Tabela";
import Separator from "../../core/components/Separator.tsx";
import InputField from "../../core/data-entry/InputField.tsx";
import { formatDateHora } from "../../core/utils/formatDate.ts";
import useService from "../../hooks/useService.ts";
import { lancarCte } from "../../services/ModuloGerenciador/Embarque";
import CustomButtonV2 from "../CustomButtonV2/CustomButtonV2.js";
import LoadingComponent from "../LoadingComponent/LoadingComponent.tsx";
import "../ModalLancarCTE/ModalLancarCTE.css";

export interface SimpleDialogProps {
	titulo: string;
	tituloBotao: string;
	tituloConfirmar: string;
	texto: string;
	dataAlteracao: string;
	open: boolean;
	onClose: () => void;
	onClick: () => void;
	onSuccess: () => void;
	onError: (error) => void;
}

function ModalLancarCTE(props: SimpleDialogProps) {
	const { profile } = useProfile();
	const { tabelaRedux } = useContext(TabelaRedux);

	const { onClose, open, onSuccess, onError } = props;

	const [cte, setCTE] = useState<string>("");
	const [dataHoraEmbarcado, setDataHoraEmbarcado] = useState<string>("");
	const [errorCte, setErrorCte] = useState<boolean>(false);
	const [errorDataHoraEmbarcado, setErrorDataHoraEmbarcado] =
		useState<boolean>(false);

	const [definindoCte, definirCte] = useService(
		() =>
			lancarCte({
				cte,
				dataHoraEmbarcado,
				token: profile?.token,
				EmbarqueId: tabelaRedux?.row?.idEmbarque,
			}),
		{
			autoStart: false,
			onData: () => {
				onSuccess();
				handleClose();
			},
			onError: (error) => {
				onError(error);
			},
		}
	);

	const handleClose = () => {
		setCTE("");
		setDataHoraEmbarcado("");
		onClose();
	};

	const handleDefinirCte = useCallback(() => {
		if (!cte) {
			setErrorCte(true);
			return;
		}
		if (!dataHoraEmbarcado) {
			setErrorDataHoraEmbarcado(true);
			return;
		}
		if (!tabelaRedux?.row?.idEmbarque) {
			onError("Embarque não encontrado.");
			return;
		}
		setErrorCte(false);
		setErrorDataHoraEmbarcado(false);
		definirCte();
	}, [cte, dataHoraEmbarcado, definirCte, onError, tabelaRedux]);

	const formattedDate = useMemo(() => {
		return tabelaRedux?.row?.dataHoraEmbarcado
			? formatDateHora(new Date(tabelaRedux?.row?.dataHoraEmbarcado))
			: "";
	}, [tabelaRedux?.row?.dataHoraEmbarcado]);

	useEffect(() => {
		setCTE(tabelaRedux?.row?.cte);
		setDataHoraEmbarcado(formattedDate);
	}, [tabelaRedux?.row?.cte, formattedDate]);

	const isLoading = useMemo(() => {
		return definindoCte || !profile?.token;
	}, [definindoCte, profile?.token]);

	return (
		<div>
			<Dialog open={open} onClose={handleClose}>
				<div className="modal-lancerCte-container">
					<DialogTitle id="alert-dialog-title">{props.titulo}</DialogTitle>
					<InputField
						type="text"
						label="CTE:"
						value={cte || ""}
						onChange={(e) => {
							setCTE(e.target.value.replace(/\D/g, ""));
							setErrorCte(false);
						}}
						error={!!errorCte}
					/>
					<Separator size="md" />
					<InputField
						type="datetime-local"
						label="Data hora:"
						value={dataHoraEmbarcado || ""}
						onChange={(e) => {
							setDataHoraEmbarcado(e.target.value);
							setErrorDataHoraEmbarcado(false);
						}}
						error={!!errorDataHoraEmbarcado}
					/>
					<Separator size="md" />
					<DialogActions>
						<CustomButtonV2
							classCss="custom-button-cadastrar"
							classText="button-texto-cadastrar"
							titulo="OK"
							cor="#0946B8"
							onClick={handleDefinirCte}
						/>
						<CustomButtonV2
							classCss="custom-button-cancelar"
							classText="button-texto-cancelar"
							titulo="Cancelar"
							cor="#0946B8"
							onClick={handleClose}
						/>
					</DialogActions>
				</div>
			</Dialog>
			{isLoading && (
				<div className="loading">
					<LoadingComponent />
				</div>
			)}
		</div>
	);
}

export default ModalLancarCTE;
